// frontend/src/components/Common/Navbar.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  FaHome,
  FaUser,
  FaSignInAlt,
  FaUserPlus,
  FaSignOutAlt,
  FaBars,
  FaTimes,
  FaBell,
  FaEnvelope,
  FaSearch,
  FaTrophy,
  FaCalendarAlt,
  FaMapMarkerAlt,
} from 'react-icons/fa';
import './Navbar.css';

const Navbar = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showNotifications && !event.target.closest('.notifications-dropdown')) {
        setShowNotifications(false);
      }
      if (showUserMenu && !event.target.closest('.user-menu-dropdown')) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showNotifications, showUserMenu]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
      setSearchQuery('');
    }
  };

  // Mock notifications data
  const mockNotifications = [
    { id: 1, type: 'booking', message: 'New booking request received', time: '10 min ago', read: false },
    { id: 2, type: 'message', message: 'New message from John Doe', time: '1 hour ago', read: false },
    { id: 3, type: 'system', message: 'System maintenance scheduled', time: '2 hours ago', read: true },
    { id: 4, type: 'review', message: 'You received a new review', time: '1 day ago', read: true },
  ];

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <nav className="navbar">
      <div className="navbar-container">
        {/* Logo */}
        <div className="navbar-brand">
          <Link to="/" className="logo">
            <FaTrophy className="logo-icon" />
            <span className="logo-text">SportsCoach</span>
          </Link>
        </div>

        {/* Search Bar (Desktop) */}
        <div className="navbar-search desktop-only">
          <form onSubmit={handleSearch} className="search-form">
            <div className="search-input-group">
              <FaSearch className="search-icon" />
              <input
                type="search"
                placeholder="Search coaches, sports, facilities..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />
            </div>
            <button type="submit" className="search-btn">
              Search
            </button>
          </form>
        </div>

        {/* Desktop Navigation */}
        <div className="navbar-links desktop-only">
          <Link to="/" className={`nav-link ${location.pathname === '/' ? 'active' : ''}`}>
            <FaHome /> Home
          </Link>
          <Link to="/sports" className={`nav-link ${location.pathname.startsWith('/sports') ? 'active' : ''}`}>
            <FaTrophy /> Sports
          </Link>
          <Link to="/facilities" className={`nav-link ${location.pathname.startsWith('/facilities') ? 'active' : ''}`}>
            <FaMapMarkerAlt /> Facilities
          </Link>
          
          {isAuthenticated && user?.userType === 'coach' && (
            <Link to="/coach/dashboard" className={`nav-link ${location.pathname.startsWith('/coach') ? 'active' : ''}`}>
              <FaCalendarAlt /> Dashboard
            </Link>
          )}
          
          {isAuthenticated && user?.userType === 'admin' && (
            <Link to="/admin" className={`nav-link ${location.pathname.startsWith('/admin') ? 'active' : ''}`}>
              <FaUser /> Admin
            </Link>
          )}
        </div>

        {/* User Actions (Desktop) */}
        <div className="navbar-actions desktop-only">
          {isAuthenticated ? (
            <>
              {/* Notifications */}
              <div className="notifications-container">
                <button
                  className="notification-btn"
                  onClick={() => setShowNotifications(!showNotifications)}
                  aria-label="Notifications"
                >
                  <FaBell />
                  {unreadCount > 0 && (
                    <span className="notification-badge">{unreadCount}</span>
                  )}
                </button>
                
                {showNotifications && (
                  <div className="notifications-dropdown">
                    <div className="notifications-header">
                      <h3>Notifications</h3>
                      {unreadCount > 0 && (
                        <button className="mark-all-read">Mark all as read</button>
                      )}
                    </div>
                    <div className="notifications-list">
                      {mockNotifications.map(notification => (
                        <div
                          key={notification.id}
                          className={`notification-item ${notification.read ? 'read' : 'unread'}`}
                        >
                          <div className="notification-content">
                            <p>{notification.message}</p>
                            <span className="notification-time">{notification.time}</span>
                          </div>
                          {!notification.read && (
                            <div className="notification-dot"></div>
                          )}
                        </div>
                      ))}
                    </div>
                    <Link to="/notifications" className="view-all-notifications">
                      View all notifications
                    </Link>
                  </div>
                )}
              </div>

              {/* Messages */}
              <button className="icon-btn" aria-label="Messages">
                <FaEnvelope />
              </button>

              {/* User Menu */}
              <div className="user-menu-container">
                <button
                  className="user-menu-btn"
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  aria-label="User menu"
                >
                  <div className="user-avatar">
                    {user?.name?.charAt(0) || 'U'}
                  </div>
                  <span className="user-name">{user?.name?.split(' ')[0] || 'User'}</span>
                </button>
                
                {showUserMenu && (
                  <div className="user-menu-dropdown">
                    <div className="user-info">
                      <div className="dropdown-avatar">
                        {user?.name?.charAt(0) || 'U'}
                      </div>
                      <div className="dropdown-user-info">
                        <strong>{user?.name || 'User'}</strong>
                        <span className="user-email">{user?.email}</span>
                        <span className="user-role">{user?.userType}</span>
                      </div>
                    </div>
                    
                    <div className="dropdown-divider"></div>
                    
                    <Link to="/profile" className="dropdown-item">
                      <FaUser /> My Profile
                    </Link>
                    
                    {user?.userType === 'coach' && (
                      <Link to="/coach/dashboard" className="dropdown-item">
                        <FaCalendarAlt /> Coach Dashboard
                      </Link>
                    )}
                    
                    {user?.userType === 'admin' && (
                      <Link to="/admin" className="dropdown-item">
                        <FaUser /> Admin Dashboard
                      </Link>
                    )}
                    
                    <Link to="/bookings" className="dropdown-item">
                      <FaCalendarAlt /> My Bookings
                    </Link>
                    
                    <Link to="/settings" className="dropdown-item">
                      <FaUser /> Settings
                    </Link>
                    
                    <div className="dropdown-divider"></div>
                    
                    <button onClick={handleLogout} className="dropdown-item logout">
                      <FaSignOutAlt /> Logout
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <Link to="/login" className="btn btn-outline btn-sm">
                <FaSignInAlt /> Login
              </Link>
              <Link to="/register" className="btn btn-primary btn-sm">
                <FaUserPlus /> Register
              </Link>
            </>
          )}
        </div>

        {/* Mobile Menu Toggle */}
        <button
          className="mobile-menu-toggle"
          onClick={() => setIsOpen(!isOpen)}
          aria-label="Toggle menu"
        >
          {isOpen ? <FaTimes /> : <FaBars />}
        </button>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="mobile-menu">
          {/* Search Bar (Mobile) */}
          <div className="mobile-search">
            <form onSubmit={handleSearch} className="search-form">
              <div className="search-input-group">
                <FaSearch className="search-icon" />
                <input
                  type="search"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="search-input"
                />
              </div>
            </form>
          </div>

          {/* Mobile Navigation Links */}
          <div className="mobile-links">
            <Link to="/" className={`mobile-link ${location.pathname === '/' ? 'active' : ''}`}>
              <FaHome /> Home
            </Link>
            <Link to="/sports" className={`mobile-link ${location.pathname.startsWith('/sports') ? 'active' : ''}`}>
              <FaTrophy /> Sports
            </Link>
            <Link to="/facilities" className={`mobile-link ${location.pathname.startsWith('/facilities') ? 'active' : ''}`}>
              <FaMapMarkerAlt /> Facilities
            </Link>
            
            {isAuthenticated && (
              <>
                <Link to="/profile" className={`mobile-link ${location.pathname === '/profile' ? 'active' : ''}`}>
                  <FaUser /> Profile
                </Link>
                <Link to="/bookings" className={`mobile-link ${location.pathname.startsWith('/bookings') ? 'active' : ''}`}>
                  <FaCalendarAlt /> Bookings
                </Link>
                
                {user?.userType === 'coach' && (
                  <Link to="/coach/dashboard" className={`mobile-link ${location.pathname.startsWith('/coach') ? 'active' : ''}`}>
                    <FaCalendarAlt /> Dashboard
                  </Link>
                )}
                
                {user?.userType === 'admin' && (
                  <Link to="/admin" className={`mobile-link ${location.pathname.startsWith('/admin') ? 'active' : ''}`}>
                    <FaUser /> Admin
                  </Link>
                )}
              </>
            )}
          </div>

          {/* Mobile Auth Actions */}
          <div className="mobile-auth">
            {isAuthenticated ? (
              <>
                <div className="mobile-user-info">
                  <div className="mobile-avatar">
                    {user?.name?.charAt(0) || 'U'}
                  </div>
                  <div className="mobile-user-details">
                    <strong>{user?.name}</strong>
                    <span>{user?.email}</span>
                  </div>
                </div>
                
                <div className="mobile-actions">
                  <button className="mobile-action-btn">
                    <FaBell /> Notifications
                    {unreadCount > 0 && (
                      <span className="mobile-badge">{unreadCount}</span>
                    )}
                  </button>
                  <button className="mobile-action-btn">
                    <FaEnvelope /> Messages
                  </button>
                </div>
                
                <button onClick={handleLogout} className="btn btn-danger btn-block">
                  <FaSignOutAlt /> Logout
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="btn btn-outline btn-block">
                  <FaSignInAlt /> Login
                </Link>
                <Link to="/register" className="btn btn-primary btn-block">
                  <FaUserPlus /> Register
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;